/**
 * Lecture d'un PNG (ISO/IEC 15948, W3C PNG 3e edition) en niveaux de gris, pour le lecteur de codes QR
 * (`qr-decode.ts`) -- pur, sans dependance : la decompression est celle du navigateur (`DecompressionStream`), le
 * reste (morceaux, CRC, filtres, entrelacement Adam7, palette, transparence) est ici.
 *
 * Pourquoi pas un `<canvas>` : une capture d'ecran est un PNG, et c'est le cas qu'il faut pouvoir PROUVER -- un
 * canevas ne se teste pas hors navigateur. Les autres formats (JPEG, WebP...) passent par le canevas (`qr-image.ts`).
 *
 * La transparence est composee sur du blanc : un code QR sombre sur fond transparent se lit comme sur une page.
 * Taille bornee AVANT decompression (`maxPixels`) et flux decompresse borne a la taille attendue : une image forgee
 * ne peut pas faire gonfler la memoire.
 */
import type { LuminanceImage } from './qr-decode.js';

const SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/** Profondeurs permises par type de couleur (tableau 11.1). */
const DEPTHS: Readonly<Record<number, readonly number[]>> = { 0: [1, 2, 4, 8, 16], 2: [8, 16], 3: [1, 2, 4, 8], 4: [8, 16], 6: [8, 16] };
const CHANNELS: Readonly<Record<number, number>> = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 };

/** Passes d'Adam7 : [colonne de depart, ligne de depart, pas en colonnes, pas en lignes]. */
const ADAM7 = [
  [0, 0, 8, 8],
  [4, 0, 8, 8],
  [0, 4, 4, 8],
  [2, 0, 4, 4],
  [0, 2, 2, 4],
  [1, 0, 2, 2],
  [0, 1, 1, 2],
] as const;

const CRC_TABLE = new Uint32Array(256);
for (let n = 0; n < 256; n++) {
  let c = n;
  for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  CRC_TABLE[n] = c >>> 0;
}

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (const byte of bytes) c = CRC_TABLE[(c ^ byte) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export function isPng(bytes: Uint8Array): boolean {
  return bytes.length >= 8 && SIGNATURE.every((byte, i) => bytes[i] === byte);
}

/** Decompresse un flux zlib, sans jamais garder plus de `limit` octets. */
async function inflate(compressed: Uint8Array, limit: number): Promise<Uint8Array> {
  const source = new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(compressed);
      controller.close();
    },
  });
  const reader = source.pipeThrough(new DecompressionStream('deflate') as unknown as ReadableWritablePair<Uint8Array, Uint8Array>).getReader();
  const output = new Uint8Array(limit);
  let length = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (length + value.length > limit) throw new Error('image PNG illisible (donnees en trop)');
      output.set(value, length);
      length += value.length;
      value.fill(0);
    }
  } catch (error) {
    output.fill(0);
    await reader.cancel().catch(() => undefined);
    throw error instanceof Error && error.message.startsWith('image PNG') ? error : new Error('image PNG illisible (compression)');
  }
  if (length !== limit) {
    output.fill(0);
    throw new Error('image PNG illisible (donnees tronquees)');
  }
  return output;
}

function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  return pa <= pb && pa <= pc ? a : pb <= pc ? b : c;
}

/** Defiltre en place les lignes d'une passe (octet de filtre en tete de chaque ligne) ; rend l'octet suivant. */
function unfilter(data: Uint8Array, offset: number, rows: number, stride: number, bpp: number): number {
  let previous = -1;
  for (let row = 0; row < rows; row++) {
    const filter = data[offset]!;
    const start = offset + 1;
    if (filter > 4) throw new Error('image PNG illisible (filtre inconnu)');
    for (let i = 0; i < stride; i++) {
      const left = i >= bpp ? data[start + i - bpp]! : 0;
      const up = previous >= 0 ? data[previous + i]! : 0;
      const upLeft = previous >= 0 && i >= bpp ? data[previous + i - bpp]! : 0;
      const predictor = filter === 1 ? left : filter === 2 ? up : filter === 3 ? (left + up) >>> 1 : filter === 4 ? paeth(left, up, upLeft) : 0;
      data[start + i] = (data[start + i]! + predictor) & 0xff;
    }
    previous = start;
    offset = start + stride;
  }
  return offset;
}

/**
 * Lit un PNG en niveaux de gris (0 noir, 255 blanc ; luminance Rec. 601, alpha compose sur blanc).
 *
 * @throws {Error} pas un PNG, morceau corrompu (CRC), en-tete hors norme, image de plus de `maxPixels` pixels,
 *   donnees tronquees ou en trop
 */
export async function decodePngLuminance(bytes: Uint8Array, maxPixels: number): Promise<LuminanceImage> {
  if (!isPng(bytes)) throw new Error('pas une image PNG');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 8;
  let header: { width: number; height: number; depth: number; color: number; interlace: number } | null = null;
  let palette: Uint8Array | null = null;
  let transparency: Uint8Array | null = null;
  const parts: Uint8Array[] = [];
  let ended = false;
  while (offset + 12 <= bytes.length && !ended) {
    const length = view.getUint32(offset);
    if (offset + 12 + length > bytes.length) throw new Error('image PNG illisible (morceau tronque)');
    const typed = bytes.subarray(offset + 4, offset + 8 + length);
    if (crc32(typed) !== view.getUint32(offset + 8 + length)) throw new Error('image PNG illisible (morceau corrompu)');
    const type = String.fromCharCode(...typed.subarray(0, 4));
    const data = typed.subarray(4);
    if (header === null && type !== 'IHDR') throw new Error('image PNG illisible (en-tete absent)');
    if (type === 'IHDR') {
      if (length !== 13 || header !== null) throw new Error('image PNG illisible (en-tete)');
      const ihdr = new DataView(data.buffer, data.byteOffset, 13);
      header = { width: ihdr.getUint32(0), height: ihdr.getUint32(4), depth: data[8]!, color: data[9]!, interlace: data[12]! };
      if (data[10] !== 0 || data[11] !== 0 || header.interlace > 1 || !(DEPTHS[header.color] ?? []).includes(header.depth)) {
        throw new Error('image PNG hors norme');
      }
      if (header.width === 0 || header.height === 0) throw new Error('image PNG vide');
      if (header.width * header.height > maxPixels) throw new Error('image trop grande');
    } else if (type === 'PLTE') {
      palette = data;
    } else if (type === 'tRNS') {
      transparency = data;
    } else if (type === 'IDAT') {
      parts.push(data);
    } else if (type === 'IEND') {
      ended = true;
    }
    offset += 12 + length;
  }
  if (header === null || parts.length === 0) throw new Error('image PNG illisible (aucune donnee)');
  const { width, height, depth, color, interlace } = header;
  if (color === 3 && palette === null) throw new Error('image PNG illisible (palette absente)');

  const bitsPerPixel = depth * CHANNELS[color]!;
  const bpp = Math.max(1, bitsPerPixel >>> 3);
  const passes = (interlace === 1 ? ADAM7 : [[0, 0, 1, 1]] as const).map(([x0, y0, dx, dy]) => {
    const columns = width > x0 ? Math.ceil((width - x0) / dx) : 0;
    const rows = height > y0 ? Math.ceil((height - y0) / dy) : 0;
    return { x0, y0, dx, dy, columns, rows, stride: Math.ceil((columns * bitsPerPixel) / 8) };
  });
  const expected = passes.reduce((sum, pass) => sum + (pass.columns > 0 ? pass.rows * (pass.stride + 1) : 0), 0);
  const compressed = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let at = 0;
  for (const part of parts) {
    compressed.set(part, at);
    at += part.length;
  }
  const raw = await inflate(compressed, expected);

  const luminance = new Uint8Array(width * height);
  const maxSample = (1 << depth) - 1;
  const sampleAt = (row: number, index: number): number => {
    if (depth === 16) return (raw[row + index * 2]! << 8) | raw[row + index * 2 + 1]!;
    if (depth === 8) return raw[row + index]!;
    const bit = index * depth;
    return (raw[row + (bit >>> 3)]! >>> (8 - depth - (bit & 7))) & maxSample;
  };
  const to8 = (value: number): number => (depth === 16 ? value >>> 8 : depth === 8 ? value : Math.round((value * 255) / maxSample));
  const trnsGray = transparency !== null && color === 0 && transparency.length >= 2 ? (transparency[0]! << 8) | transparency[1]! : -1;
  const trnsRgb = transparency !== null && color === 2 && transparency.length >= 6 ? [0, 2, 4].map((i) => (transparency![i]! << 8) | transparency![i + 1]!) : null;
  const gray = (r: number, g: number, b: number): number => 0.299 * r + 0.587 * g + 0.114 * b;
  const over = (value: number, alpha: number): number => Math.round((value * alpha + 255 * (255 - alpha)) / 255);

  try {
    let position = 0;
    for (const pass of passes) {
      if (pass.columns === 0 || pass.rows === 0) continue;
      const start = position;
      position = unfilter(raw, position, pass.rows, pass.stride, bpp);
      for (let y = 0; y < pass.rows; y++) {
        const row = start + y * (pass.stride + 1) + 1;
        for (let x = 0; x < pass.columns; x++) {
          const channels = CHANNELS[color]!;
          let value: number;
          if (color === 0) {
            const sample = sampleAt(row, x);
            value = sample === trnsGray ? 255 : to8(sample);
          } else if (color === 3) {
            const index = sampleAt(row, x);
            if (index * 3 + 2 >= palette!.length) throw new Error('image PNG illisible (couleur hors palette)');
            const alpha = transparency !== null && index < transparency.length ? transparency[index]! : 255;
            value = over(gray(palette![index * 3]!, palette![index * 3 + 1]!, palette![index * 3 + 2]!), alpha);
          } else if (color === 4) {
            value = over(to8(sampleAt(row, x * channels)), to8(sampleAt(row, x * channels + 1)));
          } else {
            const rgb = [0, 1, 2].map((c) => sampleAt(row, x * channels + c));
            const opaque = color === 6 ? to8(sampleAt(row, x * channels + 3)) : trnsRgb !== null && rgb.every((v, c) => v === trnsRgb[c]) ? 0 : 255;
            value = over(gray(to8(rgb[0]!), to8(rgb[1]!), to8(rgb[2]!)), opaque);
          }
          luminance[(pass.y0 + y * pass.dy) * width + pass.x0 + x * pass.dx] = Math.max(0, Math.min(255, Math.round(value)));
        }
      }
    }
  } catch (error) {
    luminance.fill(0);
    throw error;
  } finally {
    raw.fill(0);
    compressed.fill(0);
  }
  return { width, height, luminance };
}
